import { NETXL_CALENDAR, localTimeOnDay } from "@helpdesk/sla";

/**
 * The instant a Lane due date means.
 *
 * The desk's rule is that work is late from four o'clock on the day it was
 * due, and `apps/api/src/lane/overdue.ts` reads a stored due date for its date
 * alone and builds the deadline from that. This writes what that will read
 * back, so Lane shows the same moment Pulse judges against instead of an hour
 * somebody has to know to ignore.
 *
 * **Not `new Date(\`${date}T16:00:00\`)`**, which is what the task dialog did
 * with five o'clock and is wrong away from this office: that parses in the
 * browser's own zone, so an agent on a laptop set to Los Angeles wrote an
 * instant that is the *next* day in London, and the task fell due a day late.
 *
 * Noon UTC is the anchor because it is never near a day boundary in London --
 * midday in UTC is midday or one o'clock here, on the same date, in either
 * half of the year. `localTimeOnDay` then does the part that actually needs
 * care, which is that four o'clock is a different instant in July and January.
 */
export function dueAtOn(date: string): string {
  return localTimeOnDay(
    new Date(`${date}T12:00:00Z`),
    DEADLINE_MINUTES,
    NETXL_CALENDAR.timeZone,
  ).toISOString();
}

/**
 * The same, `days` from today. "Due in three days" is a date, not an interval:
 * raising one at nine in the morning and one at four in the afternoon should
 * put both on the same day, which adding milliseconds does not.
 */
export function dueAtInDays(days: number): string {
  const day = new Date();
  day.setDate(day.getDate() + days);

  return localTimeOnDay(day, DEADLINE_MINUTES, NETXL_CALENDAR.timeZone).toISOString();
}

/** Four o'clock, as minutes from midnight. Mirrors `DEADLINE_MINUTES` on the server. */
const DEADLINE_MINUTES = 16 * 60;
